import { useState, useEffect } from "react";
import { Link } from "react-router";
import {
  Bell,
  CheckCheck,
  Settings,
  Filter,
  Trash2,
  CheckSquare,
  Square,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  RotateCcw,
} from "lucide-react";
import { useNotifications } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/authContext";
import NotificationItem from "../../components/notifications/NotificationItem";

export default function NotificationsPage() {
  const { user } = useAuth();
  const {
    unreadCount,
    notifications,
    totalCount,
    page,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
  } = useNotifications();

  // Local filter states
  const [readStatusFilter, setReadStatusFilter] = useState("all"); // "all" | "unread" | "read"
  const [typeFilter, setTypeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Load notifications when filters or page changes
  useEffect(() => {
    const filters = {};
    if (readStatusFilter === "unread") filters.is_read = false;
    if (readStatusFilter === "read") filters.is_read = true;
    if (typeFilter) filters.notification_type = typeFilter;
    if (priorityFilter) filters.priority = priorityFilter;

    fetchNotifications({
      targetPage: currentPage,
      append: false,
      activeFilters: filters,
    });
    // Reset selection on page or filter change
    setSelectedIds([]);
  }, [currentPage, readStatusFilter, typeFilter, priorityFilter, fetchNotifications]);

  const toggleSelectAll = () => {
    if (selectedIds.length === notifications.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(notifications.map((n) => n.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedIds.length === 0) return;
    await markAllAsRead(selectedIds);
    setSelectedIds([]);
  };

  const handleResetFilters = () => {
    setReadStatusFilter("all");
    setTypeFilter("");
    setPriorityFilter("");
    setCurrentPage(1);
  };

  const preferencesLink = user?.user_type
    ? `/${user.user_type}/settings`
    : "/settings";

  const totalPages = Math.max(1, Math.ceil(totalCount / 15));

  const typeOptions = [
    { value: "", label: "All Notification Types" },
    { value: "quiz_reminder", label: "Quiz Reminders" },
    { value: "quiz_deadline", label: "Quiz Deadlines" },
    { value: "quiz_result", label: "Quiz Results" },
    { value: "course_enrolled", label: "Course Enrolments" },
    { value: "student_activity", label: "Student Activity" },
    { value: "quiz_completed_alert", label: "Quiz Completed Alerts" },
    { value: "subscription_activated", label: "Subscription Activated" },
    { value: "payment_successful", label: "Payment Successful" },
    { value: "payment_failed", label: "Payment Failed" },
    { value: "general_announcement", label: "Announcements" },
    { value: "marketing_promo", label: "Promotions & Offers" },
  ];

  const priorityOptions = [
    { value: "", label: "All Priorities" },
    { value: "urgent", label: "Urgent" },
    { value: "high", label: "High" },
    { value: "normal", label: "Normal" },
    { value: "low", label: "Low" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-8 space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-base-200">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-base-content flex items-center gap-2">
                Notifications
                {unreadCount > 0 && (
                  <span className="badge badge-primary badge-sm text-xs">
                    {unreadCount} unread
                  </span>
                )}
              </h1>
              <p className="text-xs text-base-content/60 mt-0.5">
                Review your course alerts, quiz notifications, and platform updates.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAllAsRead()}
              className="btn btn-outline btn-sm btn-primary gap-1 text-xs"
            >
              <CheckCheck className="w-4 h-4" />
              <span>Mark all as read</span>
            </button>
          )}

          <Link
            to={preferencesLink}
            className="btn btn-ghost btn-sm gap-1 text-xs text-base-content/70 hover:text-base-content"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Preferences</span>
          </Link>
        </div>
      </div>

      {/* Filter and Control Bar */}
      <div className="bg-base-100 p-4 rounded-2xl border border-base-200 shadow-xs space-y-4">
        {/* Status Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="join bg-base-200/60 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => {
                setReadStatusFilter("all");
                setCurrentPage(1);
              }}
              className={`join-item btn btn-xs sm:btn-sm rounded-lg border-none ${
                readStatusFilter === "all" ? "btn-primary text-white" : "btn-ghost"
              }`}
            >
              All ({totalCount})
            </button>
            <button
              type="button"
              onClick={() => {
                setReadStatusFilter("unread");
                setCurrentPage(1);
              }}
              className={`join-item btn btn-xs sm:btn-sm rounded-lg border-none ${
                readStatusFilter === "unread" ? "btn-primary text-white" : "btn-ghost"
              }`}
            >
              Unread
            </button>
            <button
              type="button"
              onClick={() => {
                setReadStatusFilter("read");
                setCurrentPage(1);
              }}
              className={`join-item btn btn-xs sm:btn-sm rounded-lg border-none ${
                readStatusFilter === "read" ? "btn-primary text-white" : "btn-ghost"
              }`}
            >
              Read
            </button>
          </div>

          {/* Quick Select and Batch Actions */}
          {notifications.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleSelectAll}
                className="btn btn-ghost btn-xs text-xs gap-1"
              >
                {selectedIds.length === notifications.length && notifications.length > 0 ? (
                  <CheckSquare className="w-3.5 h-3.5 text-primary" />
                ) : (
                  <Square className="w-3.5 h-3.5" />
                )}
                <span>Select all on page</span>
              </button>

              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={handleMarkSelectedAsRead}
                  className="btn btn-sm btn-primary text-xs gap-1"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span>Mark {selectedIds.length} read</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Dropdown Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-2 border-t border-base-200">
          <div>
            <label className="text-xs font-semibold text-base-content/70 block mb-1">
              Filter by Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="select select-bordered select-sm w-full text-xs rounded-xl"
            >
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-base-content/70 block mb-1">
              Filter by Priority
            </label>
            <select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="select select-bordered select-sm w-full text-xs rounded-xl"
            >
              {priorityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            {(typeFilter || priorityFilter || readStatusFilter !== "all") && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="btn btn-ghost btn-sm text-xs gap-1 w-full text-base-content/70 hover:text-base-content"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Reset Filters</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <div
                key={n}
                className="p-4 rounded-xl border border-base-200 bg-base-100 flex items-start gap-3 animate-pulse"
              >
                <div className="w-9 h-9 rounded-xl bg-base-300 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-base-300 rounded w-1/3" />
                  <div className="h-3 bg-base-300 rounded w-3/4" />
                  <div className="h-2.5 bg-base-300 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center bg-base-100 rounded-2xl border border-base-200 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center text-base-content/40 mb-3">
              <Sparkles className="w-8 h-8" />
            </div>
            <h3 className="text-base font-semibold text-base-content">
              No notifications found
            </h3>
            <p className="text-xs text-base-content/60 mt-1 max-w-sm">
              {typeFilter || priorityFilter || readStatusFilter !== "all"
                ? "No notifications match your active filter settings. Try clearing your filters."
                : "You do not have any notifications at the moment."}
            </p>
            {(typeFilter || priorityFilter || readStatusFilter !== "all") && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="btn btn-outline btn-sm btn-primary mt-4 text-xs"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          notifications.map((item) => (
            <div key={item.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label="Select notification"
                checked={selectedIds.includes(item.id)}
                onChange={() => toggleSelectOne(item.id)}
                className="checkbox checkbox-primary checkbox-sm rounded"
              />
              <div className="flex-1">
                <NotificationItem
                  notification={item}
                  compact={false}
                  onMarkAsRead={markAsRead}
                  onDismiss={dismissNotification}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {!isLoading && totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-base-200">
          <p className="text-xs text-base-content/60">
            Page {currentPage} of {totalPages} ({totalCount} total)
          </p>

          <div className="join">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="join-item btn btn-sm btn-outline text-xs gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="join-item btn btn-sm btn-outline text-xs gap-1"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
